import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminResources, adminUploads, formatError } from "../api";
import { Button, Card, Input, PageHeader, Spinner, Switch, Textarea } from "../ui";

const empty = {
  title: "",
  description: "",
  category: "",
  file_url: "",
  filename: "",
  file_size_bytes: 0,
  published: true,
  type: "pdf",
};

export default function ResourceEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id;
  const [draft, setDraft] = useState(isNew ? empty : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminResources.get(id).then(setDraft).catch(() => setDraft(false));
  }, [id, isNew]);

  if (draft === false) return <p className="text-sm text-red-600">Resource not found.</p>;
  if (!draft) return <Spinner />;

  const setField = (k) => (v) => setDraft({ ...draft, [k]: v });

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const r = await adminUploads.upload(f, "resource_file");
      setDraft({ ...draft, file_url: r.url, filename: r.original_filename || r.filename, file_size_bytes: r.size_bytes });
      toast.success("File uploaded.");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!draft.title || draft.title.trim().length < 2) return toast.error("Title is required");
    setBusy(true);
    try {
      const res = isNew ? await adminResources.create(draft) : await adminResources.update(id, draft);
      toast.success(isNew ? "Resource created." : "Resource saved.");
      if (isNew) nav(`/admin/resources/${res.id}`, { replace: true });
      else setDraft(res);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-resource-editor">
      <Link to="/admin/resources" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] mb-4"><ArrowLeft className="h-3.5 w-3.5" /> All resources</Link>
      <PageHeader title={isNew ? "New resource" : "Edit resource"} actions={<Button onClick={save} loading={busy}><Save className="h-3.5 w-3.5" /> {isNew ? "Create" : "Save"}</Button>} />

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="p-6 space-y-4">
          <Input label="Title" value={draft.title} onChange={(e) => setField("title")(e.target.value)} testId="resource-title" />
          <Input label="Category" value={draft.category || ""} onChange={(e) => setField("category")(e.target.value)} placeholder="e.g. Profile, Position Paper" testId="resource-category" />
          <Textarea label="Description" rows={4} value={draft.description || ""} onChange={(e) => setField("description")(e.target.value)} testId="resource-description" />
        </Card>

        <div className="space-y-4">
          <Card className="p-6">
            <h3 className="font-display text-base font-semibold text-[hsl(var(--aoa-navy))] mb-3">Visibility</h3>
            <div className="flex items-center justify-between">
              <div className="text-sm">{draft.published ? "Public" : "Hidden"}</div>
              <Switch checked={!!draft.published} onChange={setField("published")} testId="resource-published-toggle" />
            </div>
          </Card>
          <Card className="p-6">
            <h3 className="font-display text-base font-semibold text-[hsl(var(--aoa-navy))] mb-3">File</h3>
            {draft.file_url && (
              <a href={draft.file_url} target="_blank" rel="noreferrer" className="block rounded-lg border border-slate-200 p-3 text-sm mb-3">
                <div className="font-semibold truncate">{draft.filename || draft.file_url}</div>
                <div className="text-xs text-slate-500">{draft.file_size_bytes ? `${Math.round(draft.file_size_bytes / 1024)} KB` : "—"}</div>
              </a>
            )}
            <label className="inline-flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 cursor-pointer hover:border-[hsl(var(--aoa-royal))]">
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Uploading..." : (draft.file_url ? "Replace file" : "Upload file")}
              <input type="file" hidden accept=".pdf,.docx" onChange={onUpload} data-testid="resource-file" />
            </label>
          </Card>
        </div>
      </div>
    </div>
  );
}
